import ChangeName from '../../components/common/change-name/ChangeName';
import { confirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectNameData } from '../../models/projects/ProjectNameData';
import { ProjectResponse } from '../../models/projects/ProjectResponse';
import { useAccount } from '../../contexts/account/AccountContexts';
import { changeProjectName } from '../../services/projects/ProjectService';
import { GET_PROJECT } from '../../api/query/project/keys';

const BTN_DISABLED = 'btn--disabled';
const BTN_SELECTOR = '.confirm-change-name-btn';

const updateSaveButtonState = (disabled: boolean) => {
  const btn = document.querySelector(BTN_SELECTOR) as HTMLElement;
  if (btn) {
    btn.classList.toggle(BTN_DISABLED, disabled);
  }
};

const useChangeName = ({ onRefresh }: { onRefresh: () => void }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { projectResponse, setProjectResponse } = useAccount();

  let newProjectLabel = '';
  let newProjectCountry: string | null = null;

  const resetValues = () => {
    updateSaveButtonState(true);
  };

  const changeProjectNameEvent = async (id: number) => {
    const currentProject = projectResponse?.find((project) => project.id === id);
    if (!currentProject) {
      return;
    }

    const data: ProjectNameData = {
      projectId: id,
      projectName: currentProject.firmName,
      projectLabel: newProjectLabel.trim() || currentProject.projectLabel,
      projectCountry: newProjectCountry,
    };

    try {
      await changeProjectName(data);
      const patch = { projectLabel: data.projectLabel, localization: data.projectCountry };
      // Update project list context (ProjectsPage)
      setProjectResponse((prev) =>
        prev ? prev.map((p) => (p.id === id ? { ...p, ...patch } : p)) : prev
      );
      // Update single project query cache (ProjectsPreviewPage)
      queryClient.setQueryData<ProjectResponse>([GET_PROJECT, id], (prev) =>
        prev ? { ...prev, ...patch } : prev
      );
    } catch (e) {
      console.error('Error changing name:', e);
      onRefresh();
    }
  };

  const editName = (id: number) => {
    if (!projectResponse) return;

    const currentProject = projectResponse.find((project) => project.id === id);
    if (!currentProject) return;

    newProjectLabel = currentProject.projectLabel;
    newProjectCountry = currentProject.localization;
    confirmDialog({
      message: (
        <ChangeName
          selectedProjectLabel={currentProject.projectLabel}
          selectedCountry={currentProject.localization}
          onChangeProjectLabel={(label) => (newProjectLabel = label)}
          onChangeProjectCountry={(country) => (newProjectCountry = country)}
          isSaveDisabled={updateSaveButtonState}
        />
      ),
      header: t('editProject'),
      defaultFocus: 'accept',
      rejectLabel: t('cancel'),
      acceptLabel: t('save'),
      acceptClassName: 'confirm-change-name-btn btn--disabled',
      accept: () => changeProjectNameEvent(id),
      onHide: resetValues,
    });
  };

  return { editName, resetValues };
};

export default useChangeName;
